// Imports.
import moment from 'moment-timezone';
import React from 'react';

import { isMoney } from '../Api/Types/Money';

import App from '../Config/App';
import Config from '../Config/Config';

import { Symbols } from './Symbols';
import { formatCurrency, formatUserCurrency, getFractionDigits } from './formatCurrency';

const defaultLocale = Config.get('app.locale');

/**
 * Filter for formatting date strings.
 *
 * @deprecated - use gglotto/resources/js/utils/formatters/DateFormatter.ts instead
 */
export const formatDateString = (value) => {
    if (value) {
        const date = moment(String(value)).format('MM/DD/YYYY hh:mm');
        if (date === 'Invalid date') {
            return value;
        }

        return date;
    }
};

export const formatDateDayMonthYear = (value, locale = defaultLocale) => {
    const date = moment(String(value));
    if (!date.isValid()) {
        return value;
    }
    date.locale(locale);
    return date.format('DD MMMM YYYY');
};

/**
 * Displays raw date ignoring time zone
 */
export const formatBirthdayDate = (value, locale = defaultLocale) => {
    const date = moment.utc(value).startOf('day');
    if (!date.isValid()) {
        return value;
    }
    date.locale(locale);
    return date.format('DD MMMM YYYY');
};

// Filter for formatting date strings.
export const formatDateStringShort = (value) => {
    if (value) {
        const date = moment(String(value)).format('YYYY-MM-DD');
        if (date === 'Invalid date') return value;
        return date;
    }
};

export const formatDate = (value, formatWithTimezone = false, format = 'DD-MM-YYYY') => {
    if (value) {
        let date = moment(String(value));

        if (formatWithTimezone) {
            date = date.tz(process.env.MIX_LOTTERY_TIMEZONE);
        }

        date = date.format(format);

        if (date === 'Invalid date') {
            return value;
        }

        return date;
    }

    return value;
};

export const formatDateWithTimezone = (value, timezone, format = 'DD-MM-YYYY') => {
    if (value) {
        let date = moment(String(value));

        if (timezone) {
            date = date.tz(timezone);
        }

        date = date.format(format);

        if (date === 'Invalid date') {
            return value;
        }

        return date;
    }

    return value;
};

/**
 * @deprecated - use DateFormatter.formatDateWithGmtOffset instead
 */
export const formatDateStringWithTimezone = (value, timezone) => {
    let formatTimezone = process.env.MIX_LOTTERY_TIMEZONE;
    if (timezone) {
        formatTimezone = timezone;
    }
    if (value) {
        const date = moment.utc(String(value)).tz(formatTimezone).format('DD-MM-YYYY hh:mm:ss\u00A0A\u00A0z');
        if (date === 'Invalid date') return value;
        return date;
    }
    return Symbols.EM_DASH;
};

export const formatDateStringWithTimezoneUserFriendly = (value, timezone) => {
    let formatTimezone = process.env.MIX_LOTTERY_TIMEZONE;
    if (timezone) {
        formatTimezone = timezone;
    }
    if (value) {
        const date = moment.utc(String(value)).tz(formatTimezone).format('DD/MM/YY, h:mm A');
        if (date === 'Invalid date') return value;
        return date;
    }
    return Symbols.EM_DASH;
};

export const formatDateStringLong = (value) => {
    const lang = document.getElementsByTagName('html')[0].getAttribute('lang') ?? defaultLocale;
    if (value) {
        const date = moment(String(value)).parseZone().locale(lang).format('D MMMM YYYY h:mm A z');
        if (date === 'Invalid date') return value;
        return date;
    }
};

// Format long date with timezone.
export const formatLongWithTimezone = (value, timezone) => {
    const lang = document.getElementsByTagName('html')[0].getAttribute('lang') ?? defaultLocale;
    let formatTimezone = process.env.MIX_LOTTERY_TIMEZONE;
    if (timezone) {
        formatTimezone = timezone;
    }
    if (value) {
        const date = moment.utc(String(value)).tz(formatTimezone).locale(lang).format('D MMMM YYYY h:mm A [GMT]Z');
        if (date === 'Invalid date') return value;
        return date;
    }
};

// Format date to hours and minutes only.
export const formatHour = (value, timezone) => {
    let formatTimezone = process.env.MIX_LOTTERY_TIMEZONE;
    if (timezone) {
        formatTimezone = timezone;
    }

    const date = moment.utc(String(value)).tz(formatTimezone).format('hh:mm A');
    if (date === 'Invalid date') {
        return value;
    }

    return date;
};

// Notification icon type filter.
// Warning! Should be same as in resources/views/base/site/my_account/notifications/index.blade.php
export const notificationIconType = (notificationType) => {
    const notificationIcons = {
        lotteryDraw: 'fa-dice',
        news: 'fa-newspaper',
        depositSuccesss: 'fa-money-check-alt',
        depositFail: 'fa-exclamation',
        ticketPurchase: 'fa-ticket-alt',
        bonusTicket: 'fa-ticket-alt',
        emailChanged: 'fa-envelope',
        passwordChanged: 'fa-key',
        phoneChanged: 'fa-phone',
        twoFactorAuthenticationChanged: 'fa-lock',
        manualNotification: 'fa-comment',
    };

    return notificationIcons[notificationType] ?? '';
};

export { formatCurrency };
export { formatUserCurrency };

/**
 * Convert from system currency to target currency
 * @param amount
 * @param currency
 * @returns {string}
 */
export const convertAmount = (amount, currency) => {
    const systemCurrency = Config.get('app.currency');
    let amountConverted = amount;
    if (systemCurrency !== currency) {
        const currencyRate = document.getElementsByTagName('html')[0].getAttribute('data-currency-rate');
        amountConverted = amount * currencyRate;
    }

    return formatCurrency(amountConverted, null, currency);
};

export const displayAmount = (amount, currency) => {
    return formatCurrency(amount, getFractionDigits(), currency);
};

/**
 * Use for jackpots, where .00 in millions will not be displayed.
 */
export const displayJackpotAmount = (amount, currency) => {
    return formatCurrency(amount, 0, currency);
};

export { getFractionDigits };

export const formatPercentage = (value, suffix = ' %') => {
    return value < 0.01 || value == null ? Symbols.EM_DASH : value + suffix;
};

// t -> e.g. { useTranslation } from 'react-i18next';
export const formatTaxInfo = (amount, currencyCode, fractionDigits, t) => {
    if (t === undefined) {
        let trace = '';
        if (!App.isProduction()) {
            trace = new Error().stack;
        }

        console.error('translation cannot be used: t is undefined!', trace);

        t = (text) => {
            return text;
        };
    }

    let taxInfoText = '';
    if (typeof amount === 'undefined') {
        return taxInfoText;
    }
    if (amount < 0.01) {
        return taxInfoText;
    }

    taxInfoText = t('includesValueOfTax').replace('%s', formatCurrency(amount, fractionDigits, currencyCode));

    return taxInfoText;
};

export const formatTicketAmount = (amount, currencyCode, t) => {
    const canUseTranslation = (t) => {
        return t !== undefined;
    };

    if (amount < 0.01) {
        return canUseTranslation() ? t('myTickets.FREE') : 'FREE';
    }

    return formatCurrency(amount, getFractionDigits(), currencyCode);
};

export const formatTicketAmountWithDiscount = (amount, currency_code, discount_type, discount_amount, ticket_value, t) => {
    if (discount_type != null && discount_type !== 'ticket') {
        const fractionDigits = getFractionDigits();
        let price = 0;
        if (discount_type === 'amount_discount') {
            if (parseFloat(discount_amount) > parseFloat(amount)) {
                price = parseFloat(ticket_value);
            } else {
                price = parseFloat(amount) + parseFloat(discount_amount);
            }
        }

        if (discount_type === 'percentage_discount') {
            price = (parseFloat(amount) * 100) / (100 - parseFloat(discount_amount));
        }

        return (
            <span>
                <s>{formatCurrency(price, fractionDigits, currency_code)}</s> {formatCurrency(amount, fractionDigits, currency_code)}
            </span>
        );
    }

    return formatTicketAmount(amount, currency_code, t);
};

export const formatDiscountAmount = (discount_type, discount_amount, currency_code, t) => {
    if (!discount_type) {
        return null;
    }

    const fractionDigits = getFractionDigits();

    let discountText = `${t('myTickets.discount.discount')}: `;

    // eslint-disable-next-line default-case
    switch (discount_type) {
        case 'amount_discount':
            discountText += formatCurrency(discount_amount, fractionDigits, currency_code);
            break;

        case 'percentage_discount':
            discountText += formatPercentage(discount_amount);
            break;

        case 'loyalty_points':
            discountText += formatCurrency(discount_amount, fractionDigits, currency_code);
            discountText += ` (${discount_amount} ${t('myTickets.discount.loyalty_points_symbol')})`;
            break;
    }

    return discountText;
};

export const formatDiscountAmountRaw = (discount_type, discount_amount, currency_code) => {
    if (discount_type != null) {
        if (discount_type === 'amount_discount') {
            const fractionDigits = getFractionDigits();
            return formatCurrency(discount_amount, fractionDigits, currency_code);
        }
    }

    if (discount_type === 'percentage_discount') {
        return formatPercentage(discount_amount);
    }

    return null;
};

export const formatTicketPrize = (prize, currencyCode = '') => {
    if (isMoney(prize)) {
        return prize.amount === 0 ? Symbols.EM_DASH : prize.formatted;
    }

    return prize === 0 ? Symbols.EM_DASH : formatCurrency(prize, getFractionDigits(), currencyCode);
};

export const formatTicketPrizeWithPrizeInKinds = (lines, plain = false) => {
    let hasPrizeInKind = false;
    let html = '';

    if (lines && Array.isArray(lines)) {
        lines.forEach((line) => {
            const isWon = line.status === 'won';

            if (isWon && line?.lottery_prize?.lottery_rule_tier?.lottery_rule_tier_in_kind_prize?.name) {
                hasPrizeInKind = true;
                html += ` ${line.lottery_prize.lottery_rule_tier.lottery_rule_tier_in_kind_prize.count}`;
                html += `x ${line.lottery_prize.lottery_rule_tier.lottery_rule_tier_in_kind_prize.name} `;
            }
        });
    }

    if (!hasPrizeInKind) {
        return '';
    }

    // When plain is true, return only the in-kind prize text without any leading comma or extra spaces
    if (plain) {
        return html.trim();
    }

    return `, ${html}`;
};

const lotteryPrizeCurrencyEquals = (ticket, currencyCode) => {
    if (ticket.lotteryPrizeGross.currency) {
        return currencyCode === ticket.lotteryPrizeGross.currency;
    }

    return false;
};

const userPrizeCurrencyEqualsSystemPrizeCurrency = (ticket) => {
    if (ticket.prizeGross.currency && ticket.systemPrizeGross.currency) {
        return ticket.prizeGross.currency === ticket.systemPrizeGross.currency;
    }

    return false;
};

const lineLotteryPrizeCurrencyEquals = (line, currencyCode) => {
    if (line.lottery_prize !== undefined && line.lotteryPrizePerUserGross) {
        return currencyCode === line.lotteryPrizePerUserGross.currency;
    }

    return false;
};

export const getCrmTicketPrizeNetFormatted = (ticket) => {
    if (!ticket.prizeNet || !ticket.systemPrizeNet) {
        return Symbols.EM_DASH;
    }

    if (ticket.systemPrizeNet.amount === 0) {
        return Symbols.EM_DASH;
    }

    if (userPrizeCurrencyEqualsSystemPrizeCurrency(ticket)) {
        return ticket.systemPrizeNet.formatted;
    }

    return `
        ${ticket.systemPrizeNet.formatted}
        (${ticket.prizeNet.formatted})
    `;
};

export const getCrmTicketPrizeGrossFormatted = (ticket) => {
    if (!ticket.prizeGross || !ticket.systemPrizeGross) {
        return Symbols.EM_DASH;
    }

    if (ticket.systemPrizeGross.amount === 0) {
        return Symbols.EM_DASH;
    }

    if (userPrizeCurrencyEqualsSystemPrizeCurrency(ticket)) {
        return ticket.systemPrizeGross.formatted;
    }

    return `
        ${ticket.systemPrizeGross.formatted}
        (${ticket.prizeGross.formatted})
    `;
};

export const getCrmTicketPrizeTaxFixedFormatted = (ticket) => {
    if (!ticket.prizeTaxFixed || !ticket.systemPrizeTaxFixed) {
        return Symbols.EM_DASH;
    }

    if (ticket.systemPrizeTaxFixed.amount === 0) {
        return Symbols.EM_DASH;
    }

    if (userPrizeCurrencyEqualsSystemPrizeCurrency(ticket)) {
        return ticket.systemPrizeTaxFixed.formatted;
    }

    return `
        ${ticket.systemPrizeTaxFixed.formatted}
        (${ticket.prizeTaxFixed.formatted})
    `;
};

export const getCrmTicketPrizeTaxSumFormatted = (ticket) => {
    if (!ticket.prizeTaxSum || !ticket.systemPrizeTaxSum) {
        return Symbols.EM_DASH;
    }

    if (ticket.systemPrizeTaxSum.amount === 0) {
        return Symbols.EM_DASH;
    }

    if (userPrizeCurrencyEqualsSystemPrizeCurrency(ticket)) {
        return ticket.systemPrizeTaxSum.formatted;
    }

    return `
        ${ticket.systemPrizeTaxSum.formatted}
        (${ticket.prizeTaxSum.formatted})
    `;
};

export const getTicketPrizeGrossFormatted = (ticket) => {
    const prizeGross = ticket.prizeGross;
    const prizeGrossInLotteryCurrency = ticket.lotteryPrizeGross;

    // If there is a prize-in-kind attached to any winning line, show ONLY that prize (no monetary value)
    const inKindPlain = formatTicketPrizeWithPrizeInKinds(ticket.lottery_ticket_lines, true);
    if (inKindPlain) {
        return inKindPlain;
    }

    if (!prizeGross && !prizeGrossInLotteryCurrency) {
        return Symbols.EM_DASH;
    }

    if (!prizeGrossInLotteryCurrency || !prizeGrossInLotteryCurrency.currency) {
        if (!prizeGross) {
            return Symbols.EM_DASH;
        }

        return prizeGross.formatted;
    }

    if (prizeGross.amount === 0) {
        return Symbols.EM_DASH;
    }

    const ticketPrizeWithPrizeInKindsFormatted = formatTicketPrizeWithPrizeInKinds(ticket.lottery_ticket_lines);

    if (lotteryPrizeCurrencyEquals(ticket, prizeGross.currency)) {
        return `
            ${prizeGross.formatted}
            ${ticketPrizeWithPrizeInKindsFormatted}
        `;
    }

    return `
        ${prizeGross.formatted}
        (${prizeGrossInLotteryCurrency.formatted})
        ${ticketPrizeWithPrizeInKindsFormatted}
    `;
};

export const getScratchcardPrizeGrossFormatted = (ticket) => {
    const amount = ticket.prize_gross;
    const currencyCode = ticket.prize_currency_code;

    return formatCurrency(amount, 2, currencyCode);
};

export const getScratchcardPrizeNetFormatted = (ticket) => {
    const amount = ticket.prize_net;
    const currencyCode = ticket.prize_currency_code;

    if (!amount || amount === 0) {
        return Symbols.EM_DASH;
    }

    return formatCurrency(amount, 2, currencyCode);
};

export function snakeCaseToUpperCamelCase(text) {
    let result = text.replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());

    result = result.charAt(0).toUpperCase() + result.slice(1);

    return result;
}

export const getTicketLinePrizeGrossFormatted = (line, prizeInSystemCurrency = false) => {
    const linePrizeGross = prizeInSystemCurrency ? line.systemPrizeGross : line.prizeGross;
    const lotteryPrizeGross = line.lottery_prize ? line.lotteryPrizePerUserGross : null;
    const ticketPrizeWithPrizeInKindsFormatted = formatTicketPrizeWithPrizeInKinds([line]);

    if (!linePrizeGross || !lotteryPrizeGross) {
        if (!lotteryPrizeGross) {
            return Symbols.EM_DASH;
        }

        return `
            ${lotteryPrizeGross.formatted}
            ${ticketPrizeWithPrizeInKindsFormatted}
        `;
    }

    if (linePrizeGross.amount === 0) {
        return Symbols.EM_DASH;
    }

    if (lineLotteryPrizeCurrencyEquals(line, linePrizeGross.currency)) {
        return `
            ${linePrizeGross.formatted}
            ${ticketPrizeWithPrizeInKindsFormatted}
        `;
    }

    return `
        ${linePrizeGross.formatted}
        (${lotteryPrizeGross.formatted})
        ${ticketPrizeWithPrizeInKindsFormatted}
    `;
};

export const formatDateShortWithTranslatedMonth = (value, formatWithTimezone = false) => {
    let date = moment(value);

    if (formatWithTimezone) {
        date = date.tz(process.env.MIX_LOTTERY_TIMEZONE);
    }

    return date.locale(defaultLocale).format('D MMMM');
};

export const getRelativeTimeFromDate = (value, formatWithTimezone = false) => {
    let date = moment(value);

    if (formatWithTimezone) {
        date = date.tz(process.env.MIX_LOTTERY_TIMEZONE);
    }

    return date.locale(defaultLocale).fromNow();
};

export const getDetailedRelativeTime = (value, formatWithTimezone = false) => {
    let date = moment(value);

    if (formatWithTimezone) {
        date = date.tz(process.env.MIX_LOTTERY_TIMEZONE);
    }

    const now = moment();
    const duration = moment.duration(now.diff(date));

    const days = Math.floor(duration.asDays());
    const hours = Math.floor(duration.asHours() % 24);
    const minutes = Math.floor(duration.asMinutes() % 60);

    return { days, hours, minutes };
};

export const formatDrawDate = (dateString) => {
    const timezone = process.env.MIX_LOTTERY_TIMEZONE || 'UTC';

    const formattedDate = moment(dateString).tz(timezone).format('DD MMM');
    const formattedTime = moment(dateString).tz(timezone).format('hh:mm A');

    return `${formattedDate} ${formattedTime}`;
};
